import { ArrowRightIcon, HouseIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { BotonAuth, CampoAuth } from '@/components/CampoAuth'
import { Logo } from '@/components/Logo'
import { ApiError, type FieldErrors, api, auth } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'

/**
 * La pantalla del enlace de invitación.
 *
 * No existía. Las invitaciones generaban una URL `/invitacion/:token` y esa
 * ruta caía en «no encontramos esta página»: el hogar creaba el enlace, lo
 * compartía por WhatsApp, y quien lo abría se topaba con un 404. La función que
 * convierte la app en «de los dos» estaba rota de punta a punta.
 *
 * Aquí se resuelven los dos caminos:
 *
 * - **Sin cuenta**: se crea desde este mismo enlace y se entra directo al hogar
 *   que invita. Nunca se le crea uno propio, que es lo que provocaba quedarse
 *   en dos hogares y no ver ninguno de los dos que esperaba.
 * - **Con cuenta**: se entra y se acepta. Aceptar deja ese hogar como el
 *   activo, así que la app enseña lo que la persona vino a ver.
 */
export function Invitacion() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const [modo, setModo] = useState<'crear' | 'entrar'>('crear')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [campos, setCampos] = useState<FieldErrors>({})

  const vista = useQuery({
    queryKey: ['invitacion', token],
    queryFn: () => api.get<Vista>(`/invitations/${token}`),
    retry: false,
  })

  /** Aceptar y entrar. Comparte el cierre entre los dos caminos. */
  const aceptar = async (): Promise<void> => {
    await api.post(`/invitations/${token}/accept`)
    haptic('success')
    // Arranque en frío y no navegación.
    //
    // Aceptar cambia el hogar activo, y con él todos los datos de la app. Antes
    // se vaciaba la caché y se invalidaba la sesión, pero `clear()` **borra** la
    // consulta de sesión y el `invalidateQueries` siguiente no encontraba nada
    // que refrescar: se entraba al hogar nuevo con el perfil del anterior.
    window.location.assign('/')
  }

  const enviar = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    setEnviando(true)
    setError(null)
    setCampos({})

    const datos = new FormData(event.currentTarget)
    try {
      if (modo === 'crear') {
        await auth.register({
          email: String(datos.get('email')),
          password: String(datos.get('password')),
          displayName: String(datos.get('displayName')),
          // Quien llega por una invitación viene a entrar a un hogar que ya
          // existe. El servidor exige estos campos, así que se le crea uno
          // propio que queda de reserva —vacío y en segundo plano— y el activo
          // pasa a ser el que invita en cuanto acepta.
          //
          // Se llama «Mi hogar» y no como el que invita: antes se copiaba ese
          // nombre y quedaban dos hogares llamados igual en el selector, sin
          // forma de distinguirlos.
          householdName: 'Mi hogar',
          householdKind: 'PERSONAL',
          // Con esto el servidor sabe que no toca dar la bienvenida a ese hogar
          // de reserva, que sería el primer correo de PayBae hablando de algo
          // que esta persona no pidió.
          invitationToken: token,
        })
      } else {
        await auth.login(String(datos.get('email')), String(datos.get('password')))
      }
      await aceptar()
    } catch (cause) {
      haptic('error')
      if (cause instanceof ApiError) {
        setError(cause.message)
        setCampos(cause.fieldErrors ?? {})
      } else {
        setError('Algo salió mal. Inténtalo de nuevo.')
      }
      setEnviando(false)
    }
  }

  if (vista.isPending) {
    return (
      <Marco>
        <div className="h-32 animate-pulse rounded-card bg-surface-sunken" />
      </Marco>
    )
  }

  if (vista.isError || !vista.data || vista.data.status !== 'ACTIVE') {
    const mensaje =
      vista.error instanceof ApiError
        ? vista.error.message
        : 'Esta invitación ya no está disponible. Pídele a quien te invitó que genere otra.'
    return (
      <Marco>
        <div className="flex flex-col items-center gap-3 rounded-card bg-expense-soft px-5 py-8 text-center">
          <WarningCircleIcon size={28} className="text-expense-text" />
          <p className="text-[14px] leading-snug text-expense-text">{mensaje}</p>
        </div>
        <BotonAuth type="button" variante="contorno" onClick={() => navigate('/', { replace: true })}>
          Ir a PayBae
        </BotonAuth>
      </Marco>
    )
  }

  const { householdName, invitedBy } = vista.data

  return (
    <Marco>
      <div className="flex flex-col items-center gap-3 text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-primary-soft text-primary-text">
          <HouseIcon size={26} weight="fill" />
        </span>
        <h1 className="font-display text-[26px] font-extrabold leading-[1.1] tracking-[-0.04em] text-ink">
          {invitedBy} te invita a {householdName}
        </h1>
        <p className="text-[14px] leading-snug text-ink-soft">
          Van a llevar sus finanzas juntos: cada quien registra lo suyo desde su teléfono y los
          dos ven cuánto queda.
        </p>
      </div>

      <form onSubmit={enviar} className="mt-7 flex flex-col gap-3" noValidate>
        {modo === 'crear' ? (
          <CampoAuth
            label="Tu nombre"
            name="displayName"
            autoComplete="name"
            errores={campos.displayName}
          />
        ) : null}
        <CampoAuth
          label="Tu correo"
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          errores={campos.email}
        />
        <CampoAuth
          label="Contraseña"
          name="password"
          type="password"
          autoComplete={modo === 'crear' ? 'new-password' : 'current-password'}
          ayuda={modo === 'crear' ? 'Mínimo 10 caracteres.' : undefined}
          errores={campos.password}
        />

        {error ? (
          <p role="alert" className="rounded-control bg-expense-soft px-4 py-3 text-[13px] text-expense-text">
            {error}
          </p>
        ) : null}

        <div className="mt-3">
          <BotonAuth enviando={enviando}>
            {modo === 'crear' ? 'Crear cuenta y entrar' : 'Entrar al hogar'}
          </BotonAuth>
        </div>
      </form>

      <p className="py-7 text-center text-[14px] text-ink-soft">
        {modo === 'crear' ? '¿Ya tienes cuenta?' : '¿Todavía no tienes cuenta?'}{' '}
        <button
          type="button"
          onClick={() => {
            setModo(modo === 'crear' ? 'entrar' : 'crear')
            setError(null)
            setCampos({})
          }}
          className={cn('min-h-[44px] font-semibold text-primary-text active:opacity-70')}
        >
          {modo === 'crear' ? 'Entra' : 'Créala'}
          <ArrowRightIcon size={13} weight="bold" className="ml-1 inline align-middle" />
        </button>
      </p>
    </Marco>
  )
}

/** Tal cual la devuelve `GET /api/invitations/:token`, comprobado con curl. */
interface Vista {
  householdName: string
  householdKind: string
  intendedRole: string
  invitedBy: string
  expiresAt: string
  valid: boolean
  status: string
}

function Marco({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-bg">
      <div className="px-safe mx-auto w-full max-w-sm px-6 pt-safe">
        <header className="flex justify-center py-8">
          <Logo className="size-11" />
        </header>
        {children}
      </div>
    </div>
  )
}
